import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc.js';

import type { UserDisplay } from '@/clients/v1';
import type { User } from '@/clients/v2';

dayjs.extend(utc);
dayjs.extend(timezone);

/** An instant (created_at, modified_at) shown in the user's time zone. */
export const formatDate = (
  date: string,
  hoursMinutes: boolean = true,
  format: string = 'DD/MM/YYYY',
) => {
  const parsedDate = dayjs.utc(date).local();

  return hoursMinutes ? parsedDate.format(`${format} HH:mm`) : parsedDate.format(format);
};

/** A calendar date (review_date, a due date): keeps the day stable in every time zone. */
export const formatDateUtc = (date: string, format: string = 'DD/MM/YYYY') =>
  dayjs.utc(date).format(format);

export interface CurrencyFormat {
  /** ISO 4217 code: it places the right symbol, which a symbol alone cannot ($ is several currencies). */
  currency: string;
  locale: string;
  /** Shortens what the locale shortens (1.23M), with two decimals at most: 999.99 stays whole. */
  compact?: boolean;
  maximumFractionDigits?: number;
}

/** An amount of money in the app language. */
export const formatCurrency = (
  value: number,
  { currency, locale, compact = false, maximumFractionDigits }: CurrencyFormat,
) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    ...(compact && { notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    ...(maximumFractionDigits !== undefined && { maximumFractionDigits }),
  }).format(value);

/**
 * Pads a number with a leading zero if it's less than 10.
 * Example: 5 → "05", 12 → "12"
 */
export function padWithZero(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Formats user's initials from their first and last name.
 */
export function userInitials(user: UserDisplay | User) {
  const firstName = user.firstname || '';
  const lastName = user.lastname || '';
  const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
  return initials;
}

export interface UserFullnameDTO {
  firstname: string | null;
  lastname: string | null;
}

export const toUserFullName = (
  user: UserFullnameDTO | null | undefined,
  fallback: string = '-',
) => {
  if (!user) return fallback;

  const { firstname, lastname } = user;
  return [firstname, lastname].filter(Boolean).join(' ') || fallback;
};
